import { EffectRef, Injectable, Signal, effect, inject, untracked } from '@angular/core';
import { ActivatedRouteSnapshot, ParamMap, Router, RouterStateSnapshot } from '@angular/router';
import { RouteUrls, UrlsEnum } from '@lentau/ui-kit/core/enums/route.enum';
import { WindowEventsService } from '@lentau/ui-kit/core/services/window-events/window-events.service';
import { AuthFacade } from '@lentau/auth/store';
import { AuthStepEnum } from '@lentau/auth/shared';
import { PreviousRouteService } from '@lentau/core/route';

@Injectable({ providedIn: 'root' })
export class AuthGuardService {
  private windowEventsService: WindowEventsService = inject(WindowEventsService);
  private authFacade: AuthFacade = inject(AuthFacade);
  private router: Router = inject(Router);
  private previousRouteService: PreviousRouteService = inject(PreviousRouteService);

  private authTokenCheckInProgress: Signal<boolean> = this.authFacade.authTokenCheckInProgress;
  private authTokenChecked: Signal<boolean> = this.authFacade.authTokenChecked;
  private isAuth: Signal<boolean> = this.authFacade.isAuth;

  protected readonly redirectUrl: string = UrlsEnum.Base;

  public canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    const targetUrl: string = state.url;
    const queryParams: ParamMap = next.queryParamMap;
    if (this.isAuth() && queryParams && queryParams.get('recover')) {
      this.authFacade.logout();
      this.windowEventsService.openWindow(state.url, '_self');
    }

    // Мы запоминаем урл для редиректа, если токен в процессе проверки
    if (!this.authTokenChecked()) {
      if (!this.authTokenCheckInProgress()) this.authFacade.sessionGet();

      const ref: EffectRef = effect(() => {
        const authTokenChecked: boolean = this.authTokenChecked();

        if (!authTokenChecked) return;

        untracked(() => {
          if (!this.isAuth()) {
            this.previousRouteService.setPreviousRouteActivation();
            this.redirectOnNonAuth(targetUrl);
          }
        });

        ref.destroy();
      });
      // Если токен проверен, но пользователь не авторизован мы редиректим сразу
    } else if (!this.isAuth()) {
      this.redirectOnNonAuth(targetUrl);
    }

    return true;
  }

  private redirectOnNonAuth(targetUrl: string): void {
    this.previousRouteService.setPreviousRouteActivation();
    this.authFacade.setAuthStep(AuthStepEnum.LOGIN_BY_PHONE);
    // Редирект на главную (а не на /authentication), чтобы сработал AuthPopupService
    // (он слушает NavigationEnd и открывает попап при LOGIN_BY_PHONE / OTP / выборе профиля).
    if (this.isGuestB2BProfileUrl(targetUrl)) {
      return;
    }

    void this.router.navigateByUrl(this.redirectUrl);
  }

  private isGuestB2BProfileUrl(url: string): boolean {
    const path: string = url.split('?')[0];
    const b2bMobile: string = `/${RouteUrls.MyAccountB2BMobile}`;
    const b2b: string = `/${RouteUrls.MyAccountB2B}`;

    if (path === b2bMobile || path.startsWith(`${b2bMobile}/`)) return true;
    if (path === b2b || path.startsWith(`${b2b}/`)) return true;

    return false;
  }
}
